// apps/web/app/(marketing)/contact/page.tsx
"use client";

import { useId, useState } from "react";

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const idName = useId();
  const idEmail = useId();
  const idMsg = useId();
  const idHoney = useId(); // spam trap

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    // basic honeypot
    if ((fd.get("company") as string)?.trim()) return;

    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      message: String(fd.get("message") ?? ""),
    };

    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setStatus("sent");
      (e.currentTarget as HTMLFormElement).reset();
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <header>
        <h1 className="text-3xl font-semibold">Contact</h1>
        <p className="mt-2 text-muted-foreground">We usually reply within 1–2 business days.</p>
      </header>

      <form onSubmit={onSubmit} className="card space-y-4 rounded-2xl border border-default bg-surface p-6 shadow-sm">
        {/* honeypot (hidden) */}
        <div className="hidden">
          <label htmlFor={idHoney}>Company</label>
          <input id={idHoney} name="company" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        <div>
          <label className="block text-sm font-medium" htmlFor={idName}>
            Name
          </label>
          <input
            id={idName}
            name="name"
            required
            className="mt-1 w-full rounded-lg border border-default bg-white px-3 py-2"
            autoComplete="name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium" htmlFor={idEmail}>
            Email
          </label>
          <input
            id={idEmail}
            type="email"
            name="email"
            required
            className="mt-1 w-full rounded-lg border border-default bg-white px-3 py-2"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium" htmlFor={idMsg}>
            Message
          </label>
          <textarea
            id={idMsg}
            name="message"
            rows={5}
            required
            className="mt-1 w-full rounded-lg border border-default bg-white px-3 py-2"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button type="reset" className="btn">
            Clear
          </button>
          <button type="submit" disabled={status === "sending"} className="btn btn-primary">
            {status === "sending" ? "Sending…" : "Send"}
          </button>
        </div>

        <div aria-live="polite">
          {status === "sent" && <p className="text-sm text-emerald-700">Thanks—your message was sent.</p>}
          {status === "error" && <p className="text-sm text-red-700">Sorry, something went wrong. Please try again.</p>}
        </div>
      </form>
    </div>
  );
}
